import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiFetch } from "@/lib/api";

interface FavoriteRow {
  id: number;
  foodId: number;
  defaultGrams: number | null;
  createdAt: string;
  food: {
    id: number;
    foodName: string;
    brand: string | null;
    category: string | null;
    servingSizeG: number | null;
    nutrients71: string | null;
    translations: Record<string, string> | null;
  } | null;
}

export function useFavorites() {
  const query = useQuery<FavoriteRow[]>({
    queryKey: ["/api/favorites"],
    queryFn: () => apiFetch<FavoriteRow[]>("/favorites"),
    staleTime: 60_000,
  });

  const favoriteIds = useMemo(
    () => new Set((query.data ?? []).map((f) => f.foodId)),
    [query.data],
  );

  return {
    favorites: query.data ?? [],
    favoriteIds,
    isLoading: query.isLoading,
  };
}

export function useToggleFavorite() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      foodId,
      isFavorited,
    }: {
      foodId: number;
      isFavorited: boolean;
    }) => {
      if (isFavorited) {
        await apiFetch(`/favorites/${foodId}`, { method: "DELETE" });
      } else {
        await apiFetch(`/favorites/${foodId}`, { method: "POST" });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/favorites"] });
    },
  });
}

export function useUpdateFavoriteDefaultGrams() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      foodId,
      defaultGrams,
    }: {
      foodId: number;
      defaultGrams: number | null;
    }) => {
      await apiFetch(`/favorites/${foodId}`, {
        method: "PATCH",
        body: JSON.stringify({ defaultGrams }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/favorites"] });
    },
  });
}
